import { acknowledgeConsentNotice, openConsentPreferences, getConsentSnapshot } from "./controller";
type RouterEvents = {
  on: (event: "routeChangeStart", listener: (url: string) => void) => void;
  off: (event: "routeChangeStart", listener: (url: string) => void) => void;
};
const PREFERENCE_UI = "#iubenda-iframe, #iubenda-cs-banner, [data-ouster-consent-control]";
const SCROLL_KEYS = new Set(["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "]);

/** Trusted input alone isn't a scroll: require subsequent movement of the document. */
export function installConsentInteractions(router: RouterEvents) {
  let scrollIntent: {
    x: number;
    y: number;
    at: number;
  } | undefined;
  let navigationIntent = 0;
  const element = (event: Event) => event.target instanceof Element ? event.target : null;
  const canBrowse = () => {
    const state = getConsentSnapshot();
    return state.noticeVisible && !state.preferencesPending;
  };
  const outsidePreferences = (event: Event) => !element(event)?.closest(PREFERENCE_UI);
  const onClick = (event: MouseEvent) => {
    if (!event.isTrusted) return;
    const target = element(event);
    if (target?.closest(".iubenda-cs-preferences-link")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openConsentPreferences();
      return;
    }
    if (!canBrowse() || !outsidePreferences(event)) return;
    navigationIntent = performance.now();
    if (target?.closest("a[href]")) acknowledgeConsentNotice("browsing");
  };
  const armScroll = (event: Event) => {
    if (event.isTrusted && canBrowse() && outsidePreferences(event)) {
      scrollIntent = {
        x: window.scrollX,
        y: window.scrollY,
        at: performance.now()
      };
    }
  };
  const onKey = (event: KeyboardEvent) => {
    const target = element(event);
    if (target?.closest("input, textarea, select, [contenteditable='true']") || event.ctrlKey || event.metaKey || event.altKey) return;
    if (SCROLL_KEYS.has(event.key)) armScroll(event);
  };
  const onWheel = (event: WheelEvent) => {
    if (event.deltaX || event.deltaY) armScroll(event);
  };
  const onScroll = () => {
    if (!scrollIntent || !canBrowse() || performance.now() - scrollIntent.at > 1500) return;
    if (window.scrollX !== scrollIntent.x || window.scrollY !== scrollIntent.y) {
      scrollIntent = undefined;
      acknowledgeConsentNotice("browsing");
    }
  };
  const onNavigation = (url: string) => {
    if (!navigationIntent || performance.now() - navigationIntent > 1000 || !canBrowse()) return;
    if (new URL(url, window.location.href).pathname !== window.location.pathname) acknowledgeConsentNotice("browsing");
    navigationIntent = 0;
  };
  document.addEventListener("click", onClick, true);
  document.addEventListener("auxclick", onClick, true);
  document.addEventListener("wheel", onWheel, {
    passive: true
  });
  document.addEventListener("touchmove", armScroll, {
    passive: true
  });
  document.addEventListener("keydown", onKey);
  window.addEventListener("scroll", onScroll, {
    passive: true
  });
  router.on("routeChangeStart", onNavigation);
  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("auxclick", onClick, true);
    document.removeEventListener("wheel", onWheel);
    document.removeEventListener("touchmove", armScroll);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("scroll", onScroll);
    router.off("routeChangeStart", onNavigation);
  };
}